'use client'
import { CartContent } from '@/lib/api/shopware/cart/cart.types'
import QuantityInput from '../../quantity-input'
import {
  ComponentProps,
  SetStateAction,
  useOptimistic,
  useTransition,
} from 'react'
import { useSetCart, withCartStoreSuspense } from '@/stores/cart-store'
import { updateCartItemAction } from '../cart-item/cart-item.actions'
import useSafeAction from '@/lib/safe-action/use-safe-action'
import { showToast } from '@/lib/utils/toast'

interface ItemQuantityProps
  extends Omit<ComponentProps<typeof QuantityInput>, 'value' | 'onChange'> {
  item: CartContent['lineItems'][0]
  max?: number
  isLoading?: boolean
  onLoading?: (isLoading: boolean) => void
  onValueChange?: (val: number) => void
}

const ItemQuantity = ({ item, max, isLoading, onLoading, onValueChange, ...props }: ItemQuantityProps) => {
  const [count, setCount] = useOptimistic(
    item.quantity,
    (_, count: number) => count,
  )
  const [pending, startTransition] = useTransition()
  const setCart = useSetCart()
  const { executeAsync, status } = useSafeAction(updateCartItemAction, {
    onSuccess: ({ data }) => {
      onLoading?.(false)
      if (!data) {
        return
      }
      data.isError && showToast(data.message)
    },
    onError: (e) => {
      onLoading?.(false)
    }
  })

  const handleOnChange = async (count: SetStateAction<number>) => {
    onLoading?.(true)
    onValueChange?.((count as any))
    startTransition(async () => {
      const newVal = typeof count === 'function' ? count(item.quantity) : count
      setCount(newVal)
      const res = await executeAsync({
        item: {
          id: item.id,
          referencedId: item.referencedId,
        },
        quantity: newVal,
      })
      res?.data && setCart(res.data.cart)
    })
  }

  return (
    <QuantityInput
      value={count}
      max={max}
      onChange={handleOnChange}
      loading={status === 'executing' || isLoading}
      {...props}
    />
  )
}

export default withCartStoreSuspense(ItemQuantity)
